import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { SelectProps } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { deriveHeroFrom, deriveHeroTo } from 'app/theme';
import { HeroActionButton } from 'shared/components/HeroContainer';
import type { ReactElement } from 'react';

const FILTER_FIELD_BG_TRANSLUCENT = 'rgba(248, 246, 253, 0.6)';
const FILTER_FIELD_BORDER = 'rgba(34, 28, 90, 0.10)';
const FILTER_FIELD_TEXT = '#5A5578';
const FILTER_PLACEHOLDER = '#8782A0';
const CARD_BORDER = 'rgba(34, 28, 90, 0.07)';

// ─── Hero ──────────────────────────────────────────────────────────────────

export const PageRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
});

export const HeroContentRow = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

export const HeroTitleBlock = styled(Box)({
  minWidth: 0,
  position: 'relative',
});

export const HeroTitle = styled('h1')({
  margin: 0,
  fontSize: 24,
  fontWeight: 600,
  lineHeight: '32px',
  letterSpacing: '-0.025em',
});

export const InviteMemberButton = styled(HeroActionButton)({
  position: 'absolute',
  top: 0,
  right: 0,
});

// ─── Filter toolbar ────────────────────────────────────────────────────────

/** Card wrapping the search + status + role filter row */
export const FilterToolbarCard = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 16,
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: 14,
  backgroundColor: '#fff',
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
  padding: '12px 20px',
});

export const SearchFieldBox = styled(Box)({
  position: 'relative',
  flex: '1 1 220px',
  minWidth: 180,
});

export const SearchIconWrap = styled(Box)({
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  color: FILTER_PLACEHOLDER,
  pointerEvents: 'none',
});

export const SearchInput = styled('input')(({ theme }) => ({
  width: '100%',
  height: 40,
  borderRadius: 10,
  border: `1px solid ${FILTER_FIELD_BORDER}`,
  backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
  paddingLeft: 36,
  paddingRight: 12,
  fontSize: 14,
  fontFamily: 'inherit',
  color: theme.palette.text.primary,
  outline: 'none',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  '&::placeholder': { color: FILTER_PLACEHOLDER },
  '&:focus': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}26`,
  },
}));

export const FilterSelect = styled(Select)(({ theme }) => ({
  minWidth: 160,
  height: 40,
  fontSize: 14,
  fontWeight: 500,
  flexShrink: 0,
  borderRadius: 10,
  backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingLeft: 14,
    paddingRight: '26px !important',
    paddingTop: 0,
    paddingBottom: 0,
    height: '40px !important',
  },
  '& .MuiSelect-icon': {
    width: 16,
    height: 16,
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#8782A0',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(34, 28, 90, 0.12)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: `${theme.palette.primary.main}80`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: `${theme.palette.primary.main}99`,
    borderWidth: 2,
  },
})) as unknown as <T = unknown>(props: SelectProps<T>) => ReactElement;

export const ClearFiltersLink = styled('button')(({ theme }) => ({
  flexShrink: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: theme.palette.text.disabled,
  whiteSpace: 'nowrap',
  transition: 'color 0.15s ease',
  '&:hover': { color: theme.palette.text.primary },
}));

export const ApplyButton = styled(Button)(({ theme }) => ({
  height: 32,
  fontWeight: 600,
  fontSize: 12,
  borderRadius: 10,
  flexShrink: 0,
  paddingLeft: 12,
  paddingRight: 12,
  textTransform: 'none',
  background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${theme.palette.primary.main} 50%, ${deriveHeroTo(theme.palette.primary.main)} 100%)`,
  '&:hover': {
    background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${deriveHeroFrom(theme.palette.primary.main)} 50%, ${theme.palette.primary.main} 100%)`,
  },
  '&.Mui-disabled': {
    color: '#fff',
    background: theme.palette.action.disabledBackground,
  },
}));

// ─── Table ─────────────────────────────────────────────────────────────────

export const TeamTableCard = styled(Box)({
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: 14,
  backgroundColor: '#fff',
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
  overflow: 'hidden',
});

export const MemberCellBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
});

export const MemberAvatar = styled(Avatar)({
  width: 32,
  height: 32,
  '&&': {
    fontSize: '0.625rem',
    fontWeight: 700,
    backgroundColor: 'rgba(110, 79, 247, 0.1)',
    color: '#6E4FF7',
  },
});

export const MemberNameText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'inactive',
})<{ inactive?: boolean }>(({ theme, inactive }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  textDecoration: inactive ? 'line-through' : 'none',
  opacity: inactive ? 0.55 : 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const EmailText = styled('span')(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

/** Role pill — tinted by role, matching the design's violet/sky/emerald palette */
export const RolePill = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'roleKind',
})<{ roleKind: 'Owner' | 'Admin' | 'ProjectManager' | 'TeamMember' }>(({ roleKind }) => {
  const tints: Record<string, { bg: string; fg: string }> = {
    Owner: { bg: 'rgba(139, 92, 246, 0.1)', fg: 'rgb(124, 58, 237)' },
    Admin: { bg: 'rgba(139, 92, 246, 0.1)', fg: 'rgb(124, 58, 237)' },
    ProjectManager: { bg: 'rgba(14, 165, 233, 0.1)', fg: 'rgb(2, 132, 199)' },
    TeamMember: { bg: 'rgba(16, 185, 129, 0.1)', fg: 'rgb(5, 150, 105)' },
  };
  const tint = tints[roleKind] ?? tints.TeamMember;
  return {
    height: 20,
    borderRadius: 9999,
    fontSize: '0.625rem',
    fontWeight: 500,
    backgroundColor: tint.bg,
    color: tint.fg,
    '& .MuiChip-label': { paddingLeft: 8, paddingRight: 8 },
  };
});

/** Clickable role cell for editable rows — shows the pill, or a "+ Role" add affordance */
export const RoleCellButton = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  cursor: 'pointer',
  color: theme.palette.text.secondary,
  fontSize: 12,
  fontWeight: 500,
  userSelect: 'none',
  '&:hover': { opacity: 0.8 },
}));

export const BillableCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});

export const RateValue = styled('span')(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontVariantNumeric: 'tabular-nums',
}));

/** "Add rate"/"Edit" button — dashed outline, matching the design's ghost affordance */
export const AddRateButton = styled(Button)(({ theme }) => ({
  minWidth: 0,
  height: 26,
  padding: '0 10px',
  fontSize: '0.75rem',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: 8,
  borderStyle: 'dashed',
  borderColor: 'rgba(34, 28, 90, 0.25)',
  color: FILTER_FIELD_TEXT,
  '&:hover': {
    borderColor: `${theme.palette.primary.main}66`,
    backgroundColor: `${theme.palette.primary.main}0D`,
    color: theme.palette.primary.main,
  },
}));

export const ActionCell = styled(Box)({
  display: 'flex',
  gap: 4,
  alignItems: 'center',
  justifyContent: 'flex-end',
});

/** Ghost icon button that triggers the row-actions menu */
export const RowMenuButton = styled(Button)(({ theme }) => ({
  minWidth: 0,
  width: 28,
  height: 28,
  padding: 0,
  borderRadius: 12,
  color: FILTER_PLACEHOLDER,
  '&:hover': {
    backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
    color: theme.palette.text.primary,
  },
}));

/** Row-actions menu item */
export const RowMenuItem = styled(MenuItem)({
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 400,
  minHeight: 0,
  padding: '8px 12px',
  color: '#141030',
  '&:hover': {
    backgroundColor: '#F8F6FD',
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#F8F6FD',
  },
  '& .MuiListItemText-root': { margin: 0 },
  '& .MuiListItemText-primary': { fontSize: '0.75rem' },
});

/** Destructive variant of RowMenuItem */
export const DeleteMenuItem = styled(RowMenuItem)(({ theme }) => ({
  color: theme.palette.error.main,
  '&:hover': {
    backgroundColor: `${theme.palette.error.main}1A`,
  },
  '&.Mui-focusVisible': {
    backgroundColor: `${theme.palette.error.main}1A`,
  },
}));

/** Paper/list overrides for the row-actions menu — matches the design's shadow-float spec */
export const rowMenuSlotProps = {
  paper: {
    sx: {
      minWidth: 192,
      p: 0.5,
      borderRadius: '14px',
      border: '1px solid rgba(34, 28, 90, 0.1)',
      boxShadow: '0 8px 30px -6px rgba(47,39,117,0.18), 0 2px 8px -2px rgba(34,28,90,0.06)',
    },
  },
  list: { sx: { py: 0 } },
} as const;

export const TableFooterRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderTop: `1px solid ${CARD_BORDER}`,
  padding: '12px 20px',
  fontSize: '0.75rem',
  color: FILTER_PLACEHOLDER,
});

// ─── Invite member modal ───────────────────────────────────────────────────

export const FormFieldStack = styled(Stack)({
  paddingTop: 4,
});

/** FormFieldStack variant used by the Invite Member form's field group */
export const InviteFormFieldStack = styled(FormFieldStack)({
  paddingTop: 16,
});

/** MenuProps for the Invite Member form's Role select — matches the design's dropdown spec */
export const formSelectMenuProps = {
  slotProps: {
    paper: {
      sx: {
        mt: 0.75,
        p: 0,
        borderRadius: '14px',
        border: '1px solid rgba(34, 28, 90, 0.07)',
        boxShadow: '0 8px 30px -6px rgba(47,39,117,0.18), 0 2px 8px -2px rgba(34,28,90,0.06)',
      },
    },
  },
  // MUI's Select (unlike plain Menu) forwards MenuListProps into slotProps.list internally,
  // overwriting anything passed via slotProps.list directly — so the list padding must be set here.
  MenuListProps: { sx: { padding: '4px' } },
} as const;

/** Individual option row inside the Role select's dropdown */
export const FormSelectMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '0.875rem',
  lineHeight: '20px',
  fontWeight: 400,
  color: '#141030',
  borderRadius: 8,
  padding: '6px 8px',
  minHeight: 0,
  '&:hover': {
    backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.secondary.light,
  },
  '&.Mui-selected:hover': {
    backgroundColor: theme.palette.secondary.light,
  },
}));

/** Modal title — 18px/600, matching the design's DialogTitle spec */
export const ModalTitle = styled(DialogTitle)({
  fontSize: '1.125rem',
  lineHeight: '28px',
  fontWeight: 600,
  color: '#141030',
  padding: '24px 24px 0',
});

/** Subtitle shown under a modal's DialogTitle */
export const ModalDescription = styled(Typography)({
  fontSize: '0.875rem',
  lineHeight: '20px',
  color: FILTER_FIELD_TEXT,
  padding: '0 24px',
  marginTop: 6,
});

/** Close ("x") button pinned to the modal's top-right corner */
export const ModalCloseButton = styled(IconButton)({
  position: 'absolute',
  top: 16,
  right: 16,
  width: 32,
  height: 32,
  color: FILTER_PLACEHOLDER,
  '&:hover': {
    backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
    color: FILTER_FIELD_TEXT,
  },
});

/** Fixed label above a modal form field, matching the design's separate-label pattern */
export const FormFieldLabel = styled('label')({
  display: 'block',
  fontSize: '0.75rem',
  lineHeight: '16px',
  fontWeight: 500,
  color: FILTER_FIELD_TEXT,
  marginBottom: 6,
});

export const FormDialogActions = styled(DialogActions)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(2),
}));

/** FormDialogActions variant used by the Invite Member form, matching the design's footer spacing */
export const InviteFormDialogActions = styled(FormDialogActions)({
  paddingTop: 24,
  paddingBottom: 24,
});

export const FormDialogContent = styled(DialogContent)({
  paddingTop: 0,
});

/** FormDialogContent variant used by the Invite Member form (no bottom padding above the footer) */
export const InviteFormDialogContent = styled(FormDialogContent)({
  paddingBottom: 0,
});

/** Info banner in Set Rate modal */
export const InfoBanner = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.info.main,
  color: theme.palette.info.contrastText,
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  opacity: 0.85,
}));

/** Row inside role modal: checkbox + text block */
export const RoleOptionRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  padding: `${theme.spacing(1.5)} 0`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-of-type': { borderBottom: 'none' },
}));

/** Rounded-field styling for the Invite Member form's TextFields, matching the design's input look */
export const FormTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: 10,
    backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
    fontSize: '0.875rem',
    color: '#141030',
  },
  '& .MuiSelect-select': {
    fontWeight: 500,
  },
  '& .MuiSelect-icon': {
    width: 16,
    height: 16,
    right: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    color: FILTER_PLACEHOLDER,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: FILTER_FIELD_BORDER,
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: FILTER_FIELD_BORDER,
  },
  '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
    borderColor: `${theme.palette.primary.main}4D`,
    borderWidth: 1,
  },
  '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error)': {
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`,
  },
  '& .MuiOutlinedInput-root.Mui-error.Mui-focused': {
    boxShadow: `0 0 0 2px ${theme.palette.error.main}33`,
  },
  '& .MuiInputAdornment-root': {
    color: FILTER_PLACEHOLDER,
  },
}));

export const ModalCancelButton = styled(Button)({
  height: 40,
  padding: '5px 16px',
  borderRadius: 10,
  fontWeight: 600,
  lineHeight: 1.5,
  textTransform: 'none',
  color: '#141030',
  border: '1px solid rgba(201, 182, 255, 0.4)',
  backgroundColor: '#fff',
  '&:hover': {
    backgroundColor: 'rgba(248, 246, 253, 0.6)',
    border: '1px solid rgba(201, 182, 255, 0.4)',
  },
});

export const ModalSubmitButton = styled(Button)(({ theme }) => ({
  height: 40,
  borderRadius: 10,
  fontWeight: 600,
  lineHeight: 1.5,
  textTransform: 'none',
  color: '#fff',
  background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${theme.palette.primary.main} 50%, ${deriveHeroTo(theme.palette.primary.main)} 100%)`,
  '&:hover': {
    background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${deriveHeroFrom(theme.palette.primary.main)} 50%, ${theme.palette.primary.main} 100%)`,
  },
  '&.Mui-disabled': {
    color: '#fff',
    background: theme.palette.action.disabledBackground,
  },
}));
